import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '../interfaces/api.interface';
import { UserSetting, CreateUserSettingDto } from '../interfaces';

/**
 * Service for managing user settings (Company context)
 * Backend endpoint: /api/company/users/:userId/settings
 * Supports dynamic forms with settingKey metadata
 */
@Injectable({ providedIn: 'root' })
export class CompanyUserSettingsService {
  private apiUrl = '/api/company/users';

  constructor(private http: HttpClient) {}

  /**
   * Get all settings for user with settingKey metadata
   * @param userId User ID
   * @param scope Setting scope (USER, DRIVER, CLIENT, EMPLOYEE)
   */
  getSettings(
    userId: number,
    scope?: string
  ): Observable<ApiResponse<UserSetting[]>> {
    let params = new HttpParams();
    if (scope) {
      params = params.set('scope', scope);
    }
    
    return this.http.get<ApiResponse<UserSetting[]>>(
      `${this.apiUrl}/${userId}/settings`,
      { params }
    );
  }

  /**
   * Get single setting by key
   */
  getSettingByKey(
    userId: number,
    key: string
  ): Observable<ApiResponse<{ key: string; value: string }>> {
    return this.http.get<ApiResponse<{ key: string; value: string }>>(
      `${this.apiUrl}/${userId}/settings/${key}`
    );
  }

  /**
   * Create or update single setting (upsert)
   */
  upsertSetting(
    userId: number,
    dto: CreateUserSettingDto
  ): Observable<ApiResponse<UserSetting>> {
    return this.http.post<ApiResponse<UserSetting>>(
      `${this.apiUrl}/${userId}/settings`,
      dto
    );
  }

  /**
   * Bulk upsert settings
   */
  bulkUpsertSettings(
    userId: number,
    settings: CreateUserSettingDto[]
  ): Observable<ApiResponse<UserSetting[]>> {
    return this.http.put<ApiResponse<UserSetting[]>>(
      `${this.apiUrl}/${userId}/settings/bulk`,
      settings
    );
  }

  /**
   * Delete setting by key
   */
  deleteSetting(
    userId: number,
    key: string
  ): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.apiUrl}/${userId}/settings/${key}`
    );
  }
}
